// Modelo padrão de itens de inspeção para o checklist de serviço de oficina.
// Cada oficina pode futuramente customizar essa lista (ex: via configuração
// vinda do painel central), mas este é o template padrão usado na criação
// de um novo checklist.

export const ITEM_STATUS = {
  OK: "ok",
  ATENCAO: "atencao",
  CRITICO: "critico",
  NAO_APLICAVEL: "nao_aplicavel",
};

export const ITEM_STATUS_LABELS = {
  [ITEM_STATUS.OK]: "OK",
  [ITEM_STATUS.ATENCAO]: "Atenção",
  [ITEM_STATUS.CRITICO]: "Crítico",
  [ITEM_STATUS.NAO_APLICAVEL]: "N/A",
};

export const CHECKLIST_STATUS = {
  RASCUNHO: "rascunho",
  CONCLUIDO: "concluido",
  APROVADO: "aprovado",
};

export const CHECKLIST_STATUS_LABELS = {
  [CHECKLIST_STATUS.RASCUNHO]: "Rascunho",
  [CHECKLIST_STATUS.CONCLUIDO]: "Concluído",
  [CHECKLIST_STATUS.APROVADO]: "Aprovado",
};

// AUD-B: segmento de inspeção da oficina. Exclusivo por desenho -- é uma
// string, não dois booleanos, então nunca existe o estado "carro e moto
// ligados ao mesmo tempo". Controlado pelo painel administrativo em
// Oficinas > Editar > Checklist de Entrada; ver components/ChecklistConfigGate.js.
export const SEGMENTOS = { CARRO: "carro", MOTO: "moto" };

// Categorias e itens padrão de inspeção veicular.
const DEFAULT_TEMPLATE = [
  {
    categoria: "Motor e fluidos",
    itens: [
      "Nível de óleo do motor",
      "Nível de fluido de freio",
      "Nível de água do radiador",
      "Fluido de direção hidráulica",
      "Correias e mangueiras",
      "Filtro de ar",
    ],
  },
  {
    categoria: "Freios",
    itens: [
      "Pastilhas de freio (dianteira)",
      "Pastilhas de freio (traseira)",
      "Discos de freio",
      "Freio de mão",
    ],
  },
  {
    categoria: "Pneus e rodas",
    itens: [
      "Pneu dianteiro esquerdo",
      "Pneu dianteiro direito",
      "Pneu traseiro esquerdo",
      "Pneu traseiro direito",
      "Estepe",
      "Calibragem",
      "Alinhamento e balanceamento",
    ],
  },
  {
    categoria: "Suspensão",
    itens: ["Amortecedores", "Molas", "Buchas e batentes"],
  },
  {
    categoria: "Elétrica",
    itens: [
      "Bateria",
      "Faróis e lanternas",
      "Setas e luz de freio",
      "Painel de instrumentos",
      "Limpador de para-brisa",
    ],
  },
  {
    categoria: "Itens de segurança",
    itens: ["Cintos de segurança", "Airbags (luz de alerta)", "Extintor / triângulo"],
  },
];

// Inspeção de moto -- 35 itens em 4 categorias. Conteúdo definido pelo
// dono da oficina, não inventado aqui: quem opera a moto é quem sabe o que
// precisa ser checado.
//
// O "(se aplicável)" no nome é AVISO ao mecânico; a RESPOSTA é o status
// NAO_APLICAVEL (N/A), que já existe. Os dois juntos cobrem uma frota
// mista -- CG e Biz (ar), PCX e NMAX (CVT, correia), Fazer (líquido,
// corrente) -- sem precisar de vários checklists diferentes.
const TEMPLATE_MOTO = [
  {
    categoria: "Elétrica e eletrônica",
    itens: [
      "Bateria e terminais",
      "Farol alto e baixo",
      "Lanterna e luz de freio",
      "Piscas dianteiros e traseiros",
      "Luz da placa",
      "Buzina",
      "Painel e luzes-espia",
      "Botão de partida e corta-corrente (kill switch)",
      "Sensor e cavalete lateral",
      "Sistema de partida e carga",
    ],
  },
  {
    categoria: "Freios",
    itens: [
      "Fluido de freio (nível e aspecto, se aplicável)",
      "Pastilhas dianteiras",
      "Pastilhas ou lonas traseiras",
      "Disco ou tambor dianteiro",
      "Disco ou tambor traseiro",
      "Flexíveis e cabos de freio",
      "Manete e pedal (funcionamento e folga)",
    ],
  },
  {
    categoria: "Motor e transmissão",
    itens: [
      "Óleo do motor (nível e aspecto)",
      "Vazamentos de óleo e outros fluidos",
      "Sistema de combustível e vazamentos",
      "Filtro de ar",
      "Acelerador (funcionamento, folga e retorno)",
      "Marcha lenta e funcionamento do motor",
      "Arrefecimento (nível e vazamentos, se aplicável)",
      "Radiador, mangueiras e ventoinha (se aplicável)",
      "Embreagem (funcionamento e folga, se aplicável)",
      "Corrente (estado, folga e lubrificação, se aplicável)",
      "Correia de transmissão (estado, se aplicável)",
      "Pinhão e coroa (desgaste, se aplicável)",
      "Escapamento (fixação e vazamentos)",
    ],
  },
  {
    categoria: "Ciclística, suspensão e direção",
    itens: [
      "Suspensão dianteira e retentores das bengalas",
      "Suspensão e amortecedor traseiro",
      "Balança, buchas e articulações",
      "Caixa de direção (folga e pontos de prisão)",
      "Pneus, rodas e rolamentos (estado, pressão, desgaste e deformações)",
    ],
  },
];

const TEMPLATES = {
  [SEGMENTOS.CARRO]: DEFAULT_TEMPLATE,
  [SEGMENTOS.MOTO]: TEMPLATE_MOTO,
};

// Segmento ausente, vazio ou desconhecido cai em CARRO -- o comportamento
// que o sistema sempre teve. Nenhuma oficina muda de template por omissão.
function templateDoSegmento(segmento) {
  return TEMPLATES[segmento] || DEFAULT_TEMPLATE;
}

export const FOTOS_GERAIS_ITEM_ID = "fotos-gerais";

export function buildDefaultItens(segmento) {
  const itens = [];
  templateDoSegmento(segmento).forEach((grupo) => {
    grupo.itens.forEach((nome) => {
      itens.push({
        id: `${grupo.categoria}-${nome}`.toLowerCase().replace(/[^a-z0-9]+/g, "-"),
        categoria: grupo.categoria,
        nome,
        status: null,
        observacao: "",
        fotos: [],
      });
    });
  });
  itens.push({
    id: FOTOS_GERAIS_ITEM_ID,
    categoria: "__fotos_gerais__",
    nome: "Fotos gerais do veiculo",
    status: ITEM_STATUS.NAO_APLICAVEL,
    observacao: "",
    fotos: [],
  });
  return itens;
}

// Garante que checklists salvos antes desta mudanca (sem o item especial de
// fotos gerais) recebam o item ao serem abertos para edicao, sem perder nada.
export function ensureFotosGeraisItem(itens) {
  const lista = itens || [];
  if (lista.some((i) => i.id === FOTOS_GERAIS_ITEM_ID)) return lista;
  return [
    ...lista,
    {
      id: FOTOS_GERAIS_ITEM_ID,
      categoria: "__fotos_gerais__",
      nome: "Fotos gerais do veiculo",
      status: ITEM_STATUS.NAO_APLICAVEL,
      observacao: "",
      fotos: [],
    },
  ];
}

// AUD-B -- correcao de causa, nao de sintoma.
//
// Antes esta funcao devolvia SEMPRE as categorias do template de carro, e
// quem a usava (ChecklistForm e exportChecklist) filtrava os itens por elas.
// Um checklist de moto -- cujas categorias sao outras -- renderizaria VAZIO
// na tela E no PDF: o mecanico preencheria tudo e nao apareceria nada.
//
// Passando os itens, as categorias saem do PROPRIO checklist. Efeitos:
//   - moto e carro funcionam sem a funcao saber que segmentos existem;
//   - o segmento fica congelado no registro sem precisar de campo novo
//     (o backend nao tem coluna para isso -- ver routes/checklist.py);
//   - um checklist antigo continua exibindo exatamente o que tem gravado.
//
// Sem argumento, o retorno e identico ao de antes: compatibilidade total
// com qualquer chamada que ainda nao tenha sido atualizada.
export function getCategorias(itens) {
  if (Array.isArray(itens) && itens.length > 0) {
    const vistas = [];
    itens.forEach((i) => {
      if (!i || !i.categoria) return;
      if (i.categoria === "__fotos_gerais__") return; // item especial, nao e secao
      if (vistas.indexOf(i.categoria) === -1) vistas.push(i.categoria);
    });
    if (vistas.length > 0) return vistas;
  }
  return DEFAULT_TEMPLATE.map((g) => g.categoria);
}
